import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { useAuth } from "../auth/AuthContext";
import {
  api,
  ApiError,
  type PlayerProfileMatch,
  type PlayerProfilePayload,
  type PlayerProfileTournamentRow,
} from "../lib/api";
import { isSupportUser, SUPPORT_ROLE_LABEL } from "../lib/api";
import { routes } from "../routes";
import { matchStatusLabel } from "../lib/labels";
import {
  participantStatusLabel,
  tournamentStatusLabel,
  tournamentTypeLabel,
} from "../lib/playerLabels";
import RankMovementBadge from "../components/RankMovementBadge";
import PlayerOwnAccessPanel from "../components/PlayerOwnAccessPanel";
import PlayerAccountOwnerPanel from "../components/PlayerAccountOwnerPanel";
import { formatDate, formatDateTime } from "../lib/dateFormat";
import { impersonatePlayer } from "../lib/playerImpersonation";
import { matchScoreFromWinner } from "../lib/scoreFromWinner";
import {
  accountStatusLabel,
  familyAccountPinConflict,
  pinSuccessMessage,
  reactivateSuccessMessage,
  resolveFamilyAccountPin,
} from "../lib/familyAccount";
import { useAbortableConfirm } from "../lib/useAbortableConfirm";
import { accountNotOwnedNote, canManagePlayerAccount } from "../lib/playerAccountOwnership";

/** Torneos donde juega el jugador (vigentes e historial), sin repetir: entre
 * ellos soporte elige a qué organización asignar su cuenta. */
function playerTournamentOptions(data: PlayerProfilePayload): Array<{ id: number; name: string }> {
  const seen = new Map<number, string>();
  for (const row of [...data.active_tournaments, ...data.tournament_history]) {
    if (row.tournament && !seen.has(row.tournament.id)) seen.set(row.tournament.id, row.tournament.name);
  }
  return Array.from(seen, ([id, name]) => ({ id, name }));
}

function formatGender(gender: string | null) {
  if (gender === "male") return "Masculino";
  if (gender === "female") return "Femenino";
  return "—";
}

function formatHand(hand: string | null) {
  if (hand === "right") return "Derecha";
  if (hand === "left") return "Izquierda";
  if (hand === "ambidextrous") return "Ambidiestro";
  return "—";
}

function formatPercent(value: number | null) {
  if (value === null) return "—";
  return `${Math.round(value * 100)}%`;
}

function StatTile({
  label,
  value,
  hint,
  accent,
}: {
  label: string;
  value: string | number;
  hint?: string;
  accent?: boolean;
}) {
  return (
    <div className={accent ? "metric-card metric-card--accent" : "metric-card"}>
      <span className="metric-card__label">{label}</span>
      <span className="metric-card__value">{value}</span>
      {hint && <span className="metric-card__hint">{hint}</span>}
    </div>
  );
}

function TournamentTable({
  rows,
  emptyLabel,
  showHistory,
}: {
  rows: PlayerProfileTournamentRow[];
  emptyLabel: string;
  showHistory: boolean;
}) {
  if (rows.length === 0) {
    return <p className="muted">{emptyLabel}</p>;
  }
  return (
    <div className="responsive-table-wrap">
      <table className="data-table dashboard-table">
        <thead>
          <tr>
            <th>Torneo</th>
            <th>Estado</th>
            <th>Tipo</th>
            <th>Categoría</th>
            <th>Grupo</th>
            <th>Inscripción</th>
            <th className="data-table__cell--num">Rank inicial</th>
            <th className="data-table__cell--num">Rank actual</th>
            <th>Cambio</th>
            {showHistory ? (
              <>
                <th className="data-table__cell--num">PJ</th>
                <th className="data-table__cell--num">G</th>
                <th className="data-table__cell--num">P</th>
              </>
            ) : (
              <th className="data-table__cell--num">Posición</th>
            )}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const t = row.tournament;
            const tournamentName = t?.name ?? "—";
            return (
              <tr key={`${row.participant.id}`}>
                <td className="data-table__cell--name">
                  {t ? (
                    <Link to={routes.adminTournamentDetail(t.id)}>
                      {tournamentName}
                    </Link>
                  ) : (
                    tournamentName
                  )}
                </td>
                <td>{tournamentStatusLabel(t?.status)}</td>
                <td>{tournamentTypeLabel(t?.tournament_type)}</td>
                <td>{row.current_category?.name ?? "—"}</td>
                <td>{row.group?.name ?? "—"}</td>
                <td>{participantStatusLabel(row.participant.current_status)}</td>
                <td className="data-table__cell--num">
                  {row.participant.initial_group_rank ??
                    row.participant.initial_rank ??
                    row.participant.tournament_rank ??
                    "—"}
                </td>
                <td className="data-table__cell--num">
                  {row.participant.group_position ?? row.participant.current_rank ?? "—"}
                </td>
                <td>
                  <RankMovementBadge
                    movement={row.participant.rank_movement}
                    delta={row.participant.rank_delta}
                  />
                </td>
                {showHistory ? (
                  <>
                    <td className="data-table__cell--num">
                      {row.stats.matches_played}
                    </td>
                    <td className="data-table__cell--num">{row.stats.wins}</td>
                    <td className="data-table__cell--num">{row.stats.losses}</td>
                  </>
                ) : (
                  <td className="data-table__cell--num">
                    {row.standings_position ?? "—"}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function MatchesTable({ matches }: { matches: PlayerProfileMatch[] }) {
  if (matches.length === 0) {
    return <p className="muted">Aún no hay partidos registrados.</p>;
  }
  return (
    <div className="responsive-table-wrap">
      <table className="data-table dashboard-table">
        <thead>
          <tr>
            <th>Fecha</th>
            <th>Torneo</th>
            <th>Rival</th>
            <th>Resultado</th>
            <th>Score</th>
            <th>Estado</th>
          </tr>
        </thead>
        <tbody>
          {matches.map((m) => (
            <tr key={m.id}>
              <td>
                {formatDate(m.scheduled_date)}
                {m.scheduled_time ? ` ${m.scheduled_time}` : ""}
              </td>
              <td className="data-table__cell--name">
                {m.tournament_name ?? "—"}
              </td>
              <td>{m.opponent?.display_name ?? "—"}</td>
              <td>
                {m.result === "win"
                  ? "Ganado"
                  : m.result === "loss"
                    ? "Perdido"
                    : m.result === "draw"
                      ? "Empate"
                      : m.result === "pending"
                        ? "Pendiente"
                        : m.result === "double_walkover"
                          ? "Doble W.O."
                          : "—"}
              </td>
              <td>
                {/* Eduardo 2026-10-06: el marcador se muestra desde quien ganó. */}
                {matchScoreFromWinner({
                  score_text: m.score_text,
                  winner_participant_id: m.winner_participant_id,
                  player1_participant_id: m.player1?.participant_id ?? null,
                  player2_participant_id: m.player2?.participant_id ?? null,
                }).trim() || "—"}
              </td>
              <td>{matchStatusLabel(m.status)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function AdminPlayerProfilePage() {
  const { token, impersonateWithToken, user } = useAuth();
  // Soporte Torneos123: en la ficha solo «Entrar como jugador» (Eduardo 2026-10-30).
  const supportReadOnly = isSupportUser(user);
  const navigate = useNavigate();
  const params = useParams<{ playerProfileId: string }>();
  const { currentSignal } = useAbortableConfirm([params.playerProfileId]);
  const playerId = params.playerProfileId
    ? Number(params.playerProfileId)
    : NaN;

  const [data, setData] = useState<PlayerProfilePayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [pinBusy, setPinBusy] = useState(false);
  const [impersonatingBusy, setImpersonatingBusy] = useState(false);
  const [ownAccessOpen, setOwnAccessOpen] = useState(false);
  const [reactivateBusy, setReactivateBusy] = useState(false);

  const load = useCallback(async () => {
    if (!token || !Number.isFinite(playerId)) return;
    setLoading(true);
    setError(null);
    try {
      const response = await api.players.profile(token, playerId);
      setData(response.data);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No pudimos cargar el perfil del jugador.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      setLoading(false);
    }
  }, [token, playerId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleGeneratePin() {
    if (!token || !data) return;
    const signal = currentSignal();
    setPinBusy(true);
    setError(null);
    setMessage(null);
    try {
      try {
        const response = await api.players.generatePin(token, data.profile.id);
        // Usuario corto primero: el correo puede ser uno interno que el jugador no conoce.
        setMessage(pinSuccessMessage(response.data, data.profile.display_name));
      } catch (err) {
        const familyConflict = familyAccountPinConflict(err);
        if (!familyConflict) throw err;
        // Cuenta familiar: el PIN le cambiaría la contraseña al tutor. Se
        // explica y se ofrece lo que sí sirve: «Acceso propio», cambiar la
        // contraseña familiar confirmándolo o reactivarla (Eduardo 2026-09-29).
        const outcome = await resolveFamilyAccountPin(token, data.profile, familyConflict, signal);
        if (outcome.kind === "own_access") setOwnAccessOpen(true);
        if (outcome.kind === "message") setMessage(outcome.text);
      }
      void load();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No se pudo generar el PIN.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      setPinBusy(false);
    }
  }

  // La cuenta familiar que desactivó el error viejo (archivar a un hermano):
  // sin esto nadie de la familia podía volver a entrar (Eduardo 2026-09-29).
  async function handleReactivateAccount() {
    if (!token || !data) return;
    setReactivateBusy(true);
    setError(null);
    setMessage(null);
    try {
      const response = await api.players.reactivateAccount(token, data.profile.id);
      setMessage(reactivateSuccessMessage(response.data, data.profile.display_name));
      void load();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No se pudo reactivar la cuenta.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      setReactivateBusy(false);
    }
  }

  async function handleImpersonatePlayer() {
    if (!token || !data) return;
    setImpersonatingBusy(true);
    setError(null);
    setMessage(null);
    try {
      // «Ver como jugador» de un hijo de cuenta familiar: la app abre con ESE
      // perfil activo, no con el primero de la cuenta (Eduardo 2026-09-29).
      await impersonatePlayer(token, data.profile.id, impersonateWithToken);
      navigate("/player", { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No se pudo entrar como jugador.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      setImpersonatingBusy(false);
    }
  }

  if (!Number.isFinite(playerId)) {
    return (
      <section className="card">
        <p className="form-error" role="alert">
          Identificador de jugador inválido.
        </p>
        <Link className="btn btn--ghost" to={routes.adminTournaments}>
          Volver
        </Link>
      </section>
    );
  }

  return (
    <>
      <section className="card">
        <div className="card__header-row">
          <div>
            <h1 className="card__title">
              {data ? data.profile.display_name : "Perfil del jugador"}
            </h1>
            <p className="card__subtitle">
              Historial deportivo, torneos y resultados.
            </p>
          </div>
          <div className="form__actions">
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => navigate(-1)}
            >
              Volver
            </button>
            <Link className="btn btn--ghost" to="/admin/players">
              Jugadores
            </Link>
          </div>
        </div>

        {error && (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}
        {message && <p className="form-message">{message}</p>}
        {ownAccessOpen && token && data ? (
          <PlayerOwnAccessPanel
            token={token}
            player={data.profile}
            onClose={() => {
              setOwnAccessOpen(false);
              void load();
            }}
          />
        ) : null}

        {loading && !data ? (
          <p className="muted">Cargando…</p>
        ) : data ? (
          <>
            <div className="metric-grid">
              <StatTile
                label="Categoría"
                value={data.profile.primary_category?.name ?? "—"}
              />
              <StatTile
                label="Ciudad"
                value={data.profile.city ?? "—"}
              />
              <StatTile
                label="Estado"
                value={participantStatusLabel(data.profile.active_status)}
              />
              <StatTile
                label="Género"
                value={formatGender(data.profile.gender)}
              />
              <StatTile
                label="Mano dominante"
                value={formatHand(data.profile.dominant_hand)}
              />
            </div>
            <dl className="next-match-strip" style={{ marginTop: "0.85rem" }}>
              <dt>Email</dt>
              <dd>{data.profile.email ?? "—"}</dd>
              <dt>Usuario</dt>
              <dd>
                {data.profile.username ??
                  data.linked_user?.username ??
                  data.profile.email ??
                  "—"}
              </dd>
              <dt>Teléfono</dt>
              <dd>{data.profile.phone ?? "—"}</dd>
              <dt>Cuenta</dt>
              <dd>
                {data.linked_user
                  ? data.linked_user.has_foreign_profiles
                    ? `${accountStatusLabel(data.linked_user.status)} · cuenta familiar que también usan jugadores que no dio de alta tu organización`
                    : accountStatusLabel(data.linked_user.status)
                  : "Sin cuenta vinculada"}
              </dd>
            </dl>
            {supportReadOnly ? (
              <>
                <div className="cta-row">
                  <button
                    type="button"
                    className="btn btn--compact"
                    onClick={handleImpersonatePlayer}
                    disabled={impersonatingBusy || data.profile.active_status !== "active" || !data.linked_user}
                  >
                    {impersonatingBusy ? "Entrando…" : "Entrar como jugador"}
                  </button>
                </div>
                <p className="support-money-note" role="note">
                  Como {SUPPORT_ROLE_LABEL} solo puedes entrar como el jugador (queda registrado con tu nombre en sus
                  torneos). Dentro no puedes pagar ni mover dinero; PIN, accesos y cambios a su cuenta los hace el
                  organizador.
                </p>
              </>
            ) : canManagePlayerAccount(data) ? (
              <>
                <div className="cta-row">
                  <button
                    type="button"
                    className="btn btn--compact"
                    onClick={handleImpersonatePlayer}
                    disabled={impersonatingBusy || data.profile.active_status !== "active"}
                  >
                    {impersonatingBusy ? "Entrando…" : "Entrar como jugador"}
                  </button>
                  <button
                    type="button"
                    className="btn btn--ghost btn--compact"
                    onClick={handleGeneratePin}
                    disabled={pinBusy}
                  >
                    {pinBusy ? "Generando…" : "Generar PIN"}
                  </button>
                  {/* Acceso propio y «Pasar a su propia cuenta» (Eduardo 2026-10-04):
                      con el acceso propio ya dado, «Generar PIN» va directo al
                      usuario del jugador y el panel no se abría desde la ficha. */}
                  <button
                    type="button"
                    className="btn btn--ghost btn--compact"
                    onClick={() => setOwnAccessOpen(true)}
                    title="Su propio usuario si hoy entra con la cuenta de otra persona"
                  >
                    Acceso propio…
                  </button>
                  {data.linked_user?.status === "disabled" &&
                  data.profile.active_status === "active" &&
                  !data.linked_user.has_foreign_profiles ? (
                    <button
                      type="button"
                      className="btn btn--ghost btn--compact"
                      onClick={handleReactivateAccount}
                      disabled={reactivateBusy}
                    >
                      {reactivateBusy ? "Reactivando…" : "Reactivar cuenta"}
                    </button>
                  ) : null}
                </div>
                {data.linked_user?.status === "disabled" && data.linked_user.has_foreign_profiles ? (
                  // Reactivar es tocar la cuenta: si también la usan jugadores
                  // de otra organización, solo la reactiva soporte.
                  <p className="muted" role="note" data-no-toast>
                    La cuenta con la que entra está desactivada y también la usan jugadores que no dio de alta tu
                    organización: solo la puede reactivar el soporte de Torneos123.
                  </p>
                ) : null}
              </>
            ) : (
              // Lo registró otra organización, o no es de ninguna (Opción A,
              // Eduardo 2026-09-30): sin «Entrar como jugador», «Generar PIN»
              // ni «Reactivar cuenta». Aviso fijo, no un error al pulsar.
              <p className="muted account-not-owned-note" role="note" data-no-toast>
                {accountNotOwnedNote({
                  hasOwner: data.account_has_owner !== false,
                  hasAccount: data.has_account ?? (data.linked_user !== null || data.profile.user_id !== null),
                })}
              </p>
            )}
            {data.account_ownership && token ? (
              // Solo soporte (el backend manda esto solo al superadministrador):
              // de quién es la cuenta y a qué organización asignarla.
              <PlayerAccountOwnerPanel
                token={token}
                playerProfileId={data.profile.id}
                playerName={data.profile.display_name}
                ownership={data.account_ownership}
                tournaments={playerTournamentOptions(data)}
                onChanged={() => void load()}
              />
            ) : null}
          </>
        ) : null}
      </section>

      {data && (
        <>
          <section className="card">
            <h2 className="card__title">Resumen deportivo</h2>
            <div className="metric-grid">
              <StatTile
                label="Torneos"
                value={data.summary.tournaments_total}
                hint={`${data.summary.tournaments_active} activos`}
                accent
              />
              <StatTile
                label="Partidos"
                value={data.stats.matches_played}
              />
              <StatTile
                label="Ganados"
                value={data.stats.wins}
                hint={formatPercent(data.stats.win_rate)}
              />
              <StatTile label="Perdidos" value={data.stats.losses} />
              <StatTile
                label="Sets"
                value={`${data.stats.sets_for}-${data.stats.sets_against}`}
                hint="ganados-perdidos"
              />
              <StatTile
                label="Games"
                value={`${data.stats.games_for}-${data.stats.games_against}`}
                hint={`Diferencia ${
                  data.stats.differential >= 0
                    ? `+${data.stats.differential}`
                    : data.stats.differential
                }`}
              />
            </div>
          </section>

          <section className="card">
            <h2 className="card__title">Torneos activos</h2>
            <TournamentTable
              rows={data.active_tournaments}
              emptyLabel="Sin torneos activos."
              showHistory={false}
            />
          </section>

          <section className="card">
            <h2 className="card__title">Historial de torneos</h2>
            <TournamentTable
              rows={data.tournament_history}
              emptyLabel="Aún no hay torneos terminados."
              showHistory
            />
          </section>

          <section className="card">
            <h2 className="card__title">Resultados recientes</h2>
            <MatchesTable matches={data.recent_matches} />
          </section>

          {data.recent_communications.length > 0 && (
            <section className="card">
              <h2 className="card__title">Comunicados recientes</h2>
              <ul className="dashboard-comm-list">
                {data.recent_communications.map((c) => (
                  <li key={c.id}>
                    <strong>{c.title}</strong>
                    <span className="muted">
                      {c.tournament_name ? `${c.tournament_name} · ` : ""}
                      {formatDateTime(c.published_at)}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}
    </>
  );
}
